import { parseISO } from 'date-fns';

/**
 * Shared title format: "Access Type: Book Name - Book Box Name"
 * (duplicated in calendar UIs; keep in sync)
 */
export function parseEventTitle(title) {
  if (!title) return { bookName: '', bookBox: '', accessType: '' };

  const match = String(title).match(/^(.+?):\s*(.+?)(?:\s*-\s*(.+))?$/);
  if (match) {
    return {
      accessType: match[1].trim(),
      bookName: match[2].trim(),
      bookBox: match[3] ? match[3].trim() : '',
    };
  }

  const colonSplit = String(title).split(':');
  if (colonSplit.length >= 2) {
    return {
      accessType: colonSplit[0].trim(),
      bookName: colonSplit.slice(1).join(':').trim(),
      bookBox: '',
    };
  }

  return { bookName: String(title), bookBox: '', accessType: '' };
}

export function getDateTypeLabel(dateType) {
  const m = {
    first: 'First Access',
    early: 'Early Access',
    public: 'General Access',
    openPreorder: 'Open Preorder',
    preorderClose: 'Preorder Close',
    membersOnly: 'Members only',
  };
  return m[dateType] || m.public;
}

function parseEventDate(d) {
  if (!d) return null;
  if (d instanceof Date) return isNaN(d.getTime()) ? null : d;
  try {
    const x = parseISO(d);
    return isNaN(x.getTime()) ? null : x;
  } catch {
    return null;
  }
}

/**
 * All calendar "rows" for one API event (new model: one; legacy: up to three).
 * @returns {Array<{ event: object, dateType: string, date: Date }>}
 */
export function expandEventOccurrences(event) {
  if (!event) return [];

  if (event.eventType && event.date) {
    const d = parseEventDate(event.date);
    if (!d) return [];
    return [{ event, dateType: event.eventType, date: d }];
  }

  const rows = [];
  if (event.firstDate) {
    const d = parseEventDate(event.firstDate);
    if (d) rows.push({ event, dateType: 'first', date: d });
  }
  if (event.earlyDate) {
    const d = parseEventDate(event.earlyDate);
    if (d) rows.push({ event, dateType: 'early', date: d });
  }
  if (event.publicDate) {
    const d = parseEventDate(event.publicDate);
    if (d) rows.push({ event, dateType: 'public', date: d });
  }
  return rows;
}

/**
 * @param {object[]} events
 * @param {string} query
 * @returns {Array<{ event: object, dateType: string, date: Date, bookName: string, bookBox: string, author: string }>}
 */
export function searchEventOccurrences(events, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return [];

  const out = [];
  for (const event of events || []) {
    if (!event) continue;
    const { bookName, bookBox } = parseEventTitle(event.title);
    const author = (event.author || '').trim();
    const hay = [bookName, bookBox, author, event.title || ''].filter(Boolean).join(' ').toLowerCase();
    if (!hay.includes(q)) continue;

    const occs = expandEventOccurrences(event);
    for (const occ of occs) {
      out.push({ ...occ, bookName, bookBox, author });
    }
  }

  return out.sort((a, b) => a.date.getTime() - b.date.getTime());
}
